import React, { useState, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import { cn } from '@/utils/cn';
import { Eye, Pencil } from 'lucide-react';
import { InfoTooltip } from './Tooltip';

export interface MarkdownEditorProps {
  value: string;
  onChange: (value: string) => void;
  label?: string;
  placeholder?: string;
  rows?: number;
  helperText?: string;
  tooltip?: string;
  error?: string;
  required?: boolean;
  className?: string;
}

const FORMATTING_HINTS = [
  { label: '**Bold**', insert: '**', wrap: true },
  { label: '## Heading', insert: '## ', wrap: false },
  { label: '- List', insert: '- ', wrap: false },
  { label: '1. Numbered', insert: '1. ', wrap: false },
];

export const MarkdownEditor: React.FC<MarkdownEditorProps> = ({
  value,
  onChange,
  label,
  placeholder,
  rows = 8,
  helperText,
  tooltip,
  error,
  required,
  className,
}) => {
  const [mode, setMode] = useState<'write' | 'preview'>('write');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const handleInsert = (hint: typeof FORMATTING_HINTS[0]) => {
    const ta = textareaRef.current;
    if (!ta) return;

    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const selected = value.substring(start, end);

    let newValue: string;
    let newCursor: number;

    if (hint.wrap && selected) {
      // Wrap selected text: **selected**
      newValue = value.substring(0, start) + hint.insert + selected + hint.insert + value.substring(end);
      newCursor = end + hint.insert.length * 2;
    } else if (hint.wrap) {
      // Insert markers and place cursor in middle: **|**
      newValue = value.substring(0, start) + hint.insert + hint.insert + value.substring(end);
      newCursor = start + hint.insert.length;
    } else {
      // Insert at line start
      const lineStart = value.lastIndexOf('\n', start - 1) + 1;
      newValue = value.substring(0, lineStart) + hint.insert + value.substring(lineStart);
      newCursor = start + hint.insert.length;
    }

    onChange(newValue);
    // Restore cursor position after React re-render
    setTimeout(() => {
      ta.focus();
      ta.setSelectionRange(newCursor, newCursor);
    }, 0);
  };

  return (
    <div className={cn('w-full', className)}>
      {/* Header: label + mode toggle */}
      <div className="flex items-center justify-between mb-1.5">
        <div className="flex items-center gap-1.5">
          {label && (
            <label className="text-sm font-medium text-gray-700">
              {label}
              {required && <span className="text-red-500 ml-0.5">*</span>}
            </label>
          )}
          {tooltip && <InfoTooltip content={tooltip} />}
        </div>
        <div className="flex items-center gap-0.5 bg-gray-100 rounded-md p-0.5">
          <button
            type="button"
            onClick={() => setMode('write')}
            className={cn(
              'flex items-center gap-1 px-2 py-1 rounded text-xs font-medium transition-all',
              mode === 'write'
                ? 'bg-white text-gray-900 shadow-[2px_2px_0px_#1a1a2e]'
                : 'text-gray-500 hover:text-gray-700',
            )}
          >
            <Pencil className="w-3 h-3" />
            Write
          </button>
          <button
            type="button"
            onClick={() => setMode('preview')}
            className={cn(
              'flex items-center gap-1 px-2 py-1 rounded text-xs font-medium transition-all',
              mode === 'preview'
                ? 'bg-white text-gray-900 shadow-[2px_2px_0px_#1a1a2e]'
                : 'text-gray-500 hover:text-gray-700',
            )}
          >
            <Eye className="w-3 h-3" />
            Preview
          </button>
        </div>
      </div>

      {/* Editor body */}
      <div
        className={cn(
          'rounded-xl border-2 overflow-hidden transition-colors',
          error ? 'border-red-300' : 'border-[#1a1a2e] focus-within:border-blue-400 focus-within:ring-2 focus-within:ring-blue-500/20',
        )}
      >
        {mode === 'write' ? (
          <>
            <textarea
              ref={textareaRef}
              value={value}
              onChange={(e) => onChange(e.target.value)}
              placeholder={placeholder}
              rows={rows}
              className="w-full px-3 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 outline-none resize-none bg-white"
            />
            {/* Formatting hint bar */}
            <div className="flex items-center gap-1 px-2 py-1.5 bg-gray-50 border-t-2 border-[#1a1a2e]">
              {FORMATTING_HINTS.map((hint) => (
                <button
                  key={hint.label}
                  type="button"
                  onClick={() => handleInsert(hint)}
                  className="px-2 py-0.5 text-xs text-gray-500 hover:text-gray-800 hover:bg-gray-200 rounded transition-colors font-mono"
                >
                  {hint.label}
                </button>
              ))}
            </div>
          </>
        ) : (
          <div
            className={cn(
              'px-3 py-2.5 bg-white overflow-y-auto',
              `min-h-[${rows * 1.5}rem]`,
            )}
            style={{ minHeight: `${rows * 1.5 + 1}rem`, maxHeight: `${rows * 3}rem` }}
          >
            {value ? (
              <div className="prose prose-sm max-w-none text-gray-900 prose-headings:text-gray-900 prose-headings:font-semibold prose-p:my-1 prose-ul:my-1 prose-ol:my-1 prose-li:my-0 prose-strong:text-gray-900 prose-h2:text-base prose-h3:text-sm">
                <ReactMarkdown>{value}</ReactMarkdown>
              </div>
            ) : (
              <p className="text-sm text-gray-400 italic">Nothing to preview</p>
            )}
          </div>
        )}
      </div>

      {/* Helper / error text */}
      {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
      {helperText && !error && <p className="mt-1 text-xs text-gray-400">{helperText}</p>}
    </div>
  );
};

MarkdownEditor.displayName = 'MarkdownEditor';
